import React from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  BadgeCheck,
  FileText,
  Loader2,
  Paperclip,
  Sparkles,
  Upload,
} from 'lucide-react';
import Modal from '../../components/Modal';
import ContractFileVersionFields from './ContractFileVersionFields';

export default function ContractUploadFileModal({
  canWriteContracts,
  closeUploadModal,
  contractTitle,
  handleConfirmUpload,
  handleFilePicked,
  pendingUpload,
  setPendingUpload,
  setUploadDragOver,
  showUploadModal,
  uploadAnalyzing,
  uploadBusy,
  uploadDragOver,
}) {
  return (
    <Modal
      isOpen={showUploadModal}
      onClose={closeUploadModal}
      title="Upload file"
      footer={(
        <>
          <button
            type="button"
            className="btn outline sm"
            onClick={closeUploadModal}
            disabled={uploadBusy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn primary sm"
            onClick={handleConfirmUpload}
            disabled={
              !pendingUpload
              || uploadAnalyzing
              || uploadBusy
              || (pendingUpload?.classification?.classification === 'duplicate' && !pendingUpload?.forceUpload)
            }
          >
            {uploadBusy
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Upload className="w-3.5 h-3.5" />}
            {uploadBusy ? 'Uploading…' : 'Upload'}
          </button>
        </>
      )}
    >
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!pendingUpload && (
            // Stage 1: drag-drop / browse zone. The hidden native input
            // is shared with the click-to-browse fallback so both
            // gestures end up in the same handler.
            <>
              <input
                id="contract-detail-upload-input"
                type="file"
                accept=".pdf,.docx,.doc"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  if (f) handleFilePicked(f);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => document.getElementById('contract-detail-upload-input')?.click()}
                onDragOver={(e) => { e.preventDefault(); setUploadDragOver(true); }}
                onDragLeave={() => setUploadDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setUploadDragOver(false);
                  const f = e.dataTransfer?.files?.[0];
                  if (f) handleFilePicked(f);
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  padding: '40px 20px',
                  // Highlight via thicker border + accent icon color
                  // when dragging — no surface tint so text contrast
                  // stays solid in dark mode (the previous blue-tinted
                  // background dragged the muted subtitle below
                  // readable contrast).
                  border: uploadDragOver
                    ? '2px dashed var(--text-blue)'
                    : '1.5px dashed var(--border-primary)',
                  borderRadius: 10,
                  background: 'var(--surface-alpha-secondary)',
                  cursor: 'pointer',
                  transition: 'border-color 120ms, border-width 120ms',
                  fontFamily: 'inherit',
                  width: '100%',
                }}
              >
                <Upload className="w-6 h-6" style={{ color: uploadDragOver ? 'var(--text-blue)' : 'var(--icon-secondary)' }} />
                <div style={{ fontSize: 14, fontWeight: 450 }}>
                  {uploadDragOver ? 'Drop to upload' : 'Drag a file here, or click to browse'}
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  PDF, DOCX, or DOC · up to 50MB
                </div>
              </button>
            </>
          )}

          {pendingUpload && uploadAnalyzing && (
            // Stage 2: analyzing. Hash + classify is fast, but show a
            // spinner so the modal doesn't feel frozen.
            <div
              className="row"
              style={{
                gap: 12, padding: '20px 12px',
                border: '0.5px solid var(--border-primary)',
                borderRadius: 8, alignItems: 'center',
              }}
            >
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--text-blue)' }} />
              <div style={{ minWidth: 0 }}>
                <div className="ellipsis" style={{ fontSize: 13, fontWeight: 450 }} title={pendingUpload.file.name}>
                  Analyzing {pendingUpload.file.name}…
                </div>
                <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                  Hashing the file and checking for duplicates.
                </div>
              </div>
            </div>
          )}

          {pendingUpload && !uploadAnalyzing && pendingUpload.classification && (() => {
            const cls = pendingUpload.classification.classification;
            const isDup = cls === 'duplicate';
            const isHistDup = cls === 'historical-duplicate';
            const xMatch = pendingUpload.crossContractMatch;
            return (
              <>
                {/* File summary header */}
                <div
                  className="row"
                  style={{
                    gap: 10, padding: '10px 12px',
                    border: '0.5px solid var(--border-primary)',
                    borderRadius: 8, alignItems: 'center',
                  }}
                >
                  <FileText className="w-4 h-4" style={{ color: 'var(--icon-secondary)', flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="ellipsis" style={{ fontSize: 13, fontWeight: 450 }} title={pendingUpload.file.name}>
                      {pendingUpload.file.name}
                    </div>
                    <div className="muted mono" style={{ fontSize: 11, marginTop: 2 }}>
                      {(pendingUpload.file.size / 1024).toFixed(0)} KB · sha256 {pendingUpload.fileHash.slice(0, 12)}…
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn ghost xs"
                    onClick={() => setPendingUpload(null)}
                    title="Pick a different file"
                  >
                    Replace
                  </button>
                </div>

                {/* Hard duplicate */}
                {isDup && (
                  <div className="callout red" style={{ alignItems: 'flex-start' }}>
                    <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
                    <div className="callout-body">
                      <strong>This file is already on this contract.</strong>
                      <div style={{ fontSize: 12, marginTop: 2 }}>
                        Byte-identical to the current file. Upload anyway only if you really mean to.
                      </div>
                    </div>
                  </div>
                )}

                {/* Soft historical duplicate */}
                {isHistDup && (
                  <div className="callout yellow" style={{ alignItems: 'flex-start' }}>
                    <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
                    <div className="callout-body">
                      <strong>Looks like a re-upload of an old draft.</strong>
                      <div style={{ fontSize: 12, marginTop: 2 }}>
                        This content matches a previous draft on this contract.
                      </div>
                    </div>
                  </div>
                )}

                {/* Cross-contract version match — soft warning only.
                    User explicitly clicked Upload from this contract,
                    so we don't block, just flag. */}
                {xMatch && !isDup && !isHistDup && (
                  <div className="callout yellow" style={{ alignItems: 'flex-start' }}>
                    <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
                    <div className="callout-body">
                      <strong>This file looks like a version of a different contract.</strong>
                      <div style={{ fontSize: 12, marginTop: 2 }}>
                        It strongly matches{' '}
                        <Link
                          to={`/contracts/${xMatch.contractId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--text-yellow)', textDecoration: 'underline' }}
                        >
                          {xMatch.contract?.title || 'another contract'}
                        </Link>
                        {xMatch.contract?.counterparty && <> ({xMatch.contract.counterparty})</>}.
                        You're filing it under <strong>{contractTitle || 'this contract'}</strong>. Continue if that's right.
                      </div>
                    </div>
                  </div>
                )}

                {/* Editable form — executed flag, direction, document
                    date, notes. Pre-populated from the classifier but
                    everything is overridable. */}
                <div className="stack-md" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {canWriteContracts && (
                    // Reference document (doc-46): supporting material filed
                    // alongside the contract — pricing confirmations, order
                    // forms, emails. Kept out of the draft/executed version
                    // ladder but read by structured-data runs.
                    <label
                      className="row"
                      style={{
                        gap: 10, padding: 10,
                        border: pendingUpload.isReference
                          ? '0.5px solid var(--text-purple)'
                          : '0.5px solid var(--border-primary)',
                        borderRadius: 8, cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!pendingUpload.isReference}
                        onChange={(e) => setPendingUpload((prev) => ({
                          ...prev,
                          isReference: e.target.checked,
                          // Mutually exclusive with the executed flag — a
                          // reference doc is never a contract version.
                          isExecuted: e.target.checked ? false : prev.isExecuted,
                          rescanWithAi: e.target.checked ? false : prev.rescanWithAi,
                        }))}
                      />
                      <Paperclip className="w-4 h-4" style={{ color: 'var(--text-purple)' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 450 }}>This is a reference document</div>
                        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                          Pricing sheet, order form, email, export — not a draft or executed version.
                          Data schema runs read it alongside the contract.
                        </div>
                      </div>
                    </label>
                  )}

                  {canWriteContracts && !pendingUpload.isReference && (
                    // The checkbox + the green BadgeCheck icon already
                    // convey the checked state visually. Avoid a green
                    // surface-tint background — that turned out to drag
                    // the light body text below contrast in dark mode.
                    <label
                      className="row"
                      style={{
                        gap: 10, padding: 10,
                        border: pendingUpload.isExecuted
                          ? '0.5px solid var(--text-green)'
                          : '0.5px solid var(--border-primary)',
                        borderRadius: 8, cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!pendingUpload.isExecuted}
                        onChange={(e) => setPendingUpload((prev) => ({
                          ...prev,
                          isExecuted: e.target.checked,
                          // Auto-suggest re-scan when the file becomes
                          // an executed version — the AI can pull
                          // signature dates / final fees / etc that
                          // weren't on the draft.
                          rescanWithAi: e.target.checked ? true : prev.rescanWithAi,
                        }))}
                      />
                      <BadgeCheck className="w-4 h-4" style={{ color: 'var(--text-green)' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 450 }}>This is the executed version</div>
                        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                          Promotes the file to the contract's executed slot.
                          {cls === 'executed' && ' Detector suggests this.'}
                        </div>
                      </div>
                    </label>
                  )}

                  {canWriteContracts && !pendingUpload.isReference && (
                    // Opt-in AI re-extract after upload. Default off
                    // for routine drafts so the upload stays fast;
                    // auto-checked when the file is an executed
                    // version, since that's the case where re-scan
                    // most often surfaces new info.
                    <label
                      className="row"
                      style={{
                        gap: 10, padding: 10,
                        border: '0.5px solid var(--border-primary)',
                        borderRadius: 8, cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!pendingUpload.rescanWithAi}
                        onChange={(e) => setPendingUpload((prev) => ({ ...prev, rescanWithAi: e.target.checked }))}
                      />
                      <Sparkles className="w-4 h-4" style={{ color: 'var(--text-purple)' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 450 }}>Re-scan with AI after upload</div>
                        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                          Extract dates, fees, and other fields from this file and offer them as updates.
                          {pendingUpload.isExecuted && ' Recommended for executed versions.'}
                        </div>
                      </div>
                    </label>
                  )}

                  <ContractFileVersionFields
                    documentDate={pendingUpload.documentDate}
                    direction={pendingUpload.direction}
                    fileNotes={pendingUpload.fileNotes}
                    onChange={(field, value) => setPendingUpload((prev) => {
                      if (field === 'direction') {
                        return {
                          ...prev,
                          direction: value,
                          // Auto-clear the markup notes if user switches
                          // away from inbound and the notes still say
                          // "Counterparty markup" (the default we set).
                          fileNotes: prev.fileNotes === 'Counterparty markup' && value !== 'inbound'
                            ? ''
                            : prev.fileNotes,
                        };
                      }

                      return { ...prev, [field]: value };
                    })}
                    dateHint="Date on the file itself (cover page, draft of, etc)."
                    directionHint={(
                      <>
                        Who last handled this version.
                        {cls === 'markup' && ' Detector suggests inbound (markup).'}
                      </>
                    )}
                    notesOptional
                    notesPlaceholder="Short context — e.g. 'cover email said please review by Friday'"
                  />

                  {isDup && (
                    <label
                      className="row"
                      style={{
                        gap: 10, padding: 10,
                        border: '0.5px solid var(--border-primary)',
                        borderRadius: 8, cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!pendingUpload.forceUpload}
                        onChange={(e) => setPendingUpload((prev) => ({ ...prev, forceUpload: e.target.checked }))}
                      />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 450 }}>Upload anyway</div>
                        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                          I know this is a duplicate of the current file.
                        </div>
                      </div>
                    </label>
                  )}
                </div>
              </>
            );
          })()}
        </div>
    </Modal>
  );
}
